import { useCallback, useEffect, useRef, useState } from "react";
import { invalidateDirectoryScope } from "./directoryScope";
import * as sounds from "./sounds";

/**
 * Admin-broadcast notifications — separate from the existing per-message
 * notification system (useNotifications.ts), which is about chat activity.
 * This is for occasional, deliberate "everyone should know this" announcements
 * (see DBMWorkspace.tsx's bell icon, and AdminPanel.tsx for sending one).
 *
 * Delivery is polling, not push — there's no realtime channel for this in
 * the app (CometChat's is scoped to chat, and Supabase Realtime isn't wired
 * up client-side), and an admin broadcast doesn't need millisecond
 * latency. ~25s is "practically live" for this use case.
 */

const LOCAL_SESSION_KEY = "dbm-local-session-token";
const POLL_MS = 25_000;
const HISTORY_KEY = "dbm-notify-history";
const LAST_POLLED_KEY = "dbm-notify-lastpolled-id";
const LAST_READ_KEY = "dbm-notify-lastread-id";
const MAX_HISTORY = 20;
/** Matches the server's own cutoff (see appData.js's NOTIFICATION_EXPIRY_MS)
 * — that one keeps an old broadcast from ever being FETCHED again; this one
 * makes one already sitting in a browser's cached history disappear live,
 * the moment it crosses 3 days old, without waiting for a new poll. */
const EXPIRY_MS = 3 * 24 * 60 * 60 * 1000;

const ADMIN_STATUS_URL = process.env.REACT_APP_ADMIN_STATUS_URL || "/.netlify/functions/dbm-admin-status";
const ADMIN_ADD_URL = process.env.REACT_APP_ADMIN_ADD_URL || "/.netlify/functions/dbm-admin-add";
const NOTIFY_BROADCAST_URL = process.env.REACT_APP_NOTIFY_BROADCAST_URL || "/.netlify/functions/dbm-notify-broadcast";
const NOTIFY_POLL_URL = process.env.REACT_APP_NOTIFY_POLL_URL || "/.netlify/functions/dbm-notify-poll";
const DOMAINS_LIST_URL = process.env.REACT_APP_DOMAINS_LIST_URL || "/.netlify/functions/dbm-domains-list";
const DOMAINS_SET_URL = process.env.REACT_APP_DOMAINS_SET_URL || "/.netlify/functions/dbm-domains-set";
const USER_CATEGORY_SET_URL =
  process.env.REACT_APP_USER_CATEGORY_SET_URL || "/.netlify/functions/dbm-user-category-set";

export type BroadcastNotification = { id: number; message: string; created_at: string };
export type AdminInfo = { email: string; added_by: string | null; created_at: string | null };

function token(): string | null {
  return localStorage.getItem(LOCAL_SESSION_KEY);
}

async function postJson<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Something went wrong — try again");
  return data as T;
}

export async function checkAdminStatus(): Promise<{ isAdmin: boolean; email?: string; admins: AdminInfo[] }> {
  const t = token();
  if (!t) return { isAdmin: false, admins: [] };
  return postJson(ADMIN_STATUS_URL, { token: t });
}

export async function addAdmin(email: string): Promise<AdminInfo[]> {
  const t = token();
  if (!t) throw new Error("Your session expired — sign out and back in, then try again");
  const data = await postJson<{ admins: AdminInfo[] }>(ADMIN_ADD_URL, { token: t, email });
  return data.admins;
}

export async function sendBroadcast(message: string): Promise<void> {
  const t = token();
  if (!t) throw new Error("Your session expired — sign out and back in, then try again");
  await postJson(NOTIFY_BROADCAST_URL, { token: t, message });
}

/* ---------- domain categorisation (admin) ---------- */

export type DomainCategory = "team" | "supplier" | "customer" | "partner";
export type DomainRow = {
  domain: string;
  company: string | null;
  category: DomainCategory | null;
  visible_to_external: boolean;
  /** Accounts on this domain. */
  people: number;
  /** Of those, how many have signed into chat at least once — the only
   * ones the sidebar directory can show. */
  active: number;
};
/** One person on the admin board. `category` is what actually applies;
 * `override` is set only when an admin moved them out of the category
 * their domain implies. */
export type UserRow = {
  email: string;
  uid: string | null;
  name: string;
  domain: string;
  company: string | null;
  domainCategory: DomainCategory | null;
  override: DomainCategory | null;
  category: DomainCategory | null;
};

export type DomainsResponse = {
  domains: DomainRow[];
  users: UserRow[];
  enforced: boolean;
};

export async function listDomains(): Promise<DomainsResponse> {
  const t = token();
  if (!t) return { domains: [], users: [], enforced: false };
  const data = await postJson<DomainsResponse>(DOMAINS_LIST_URL, { token: t });
  return { ...data, users: data.users || [] };
}

/** Overrides one person's category, or clears it back to their domain's
 * with a null. Echoes the whole board back, same as setDomain. */
export async function setUserCategory(
  email: string,
  category: DomainCategory | null
): Promise<DomainsResponse> {
  const t = token();
  if (!t) throw new Error("Your session expired — sign out and back in, then try again");
  const data = await postJson<DomainsResponse>(USER_CATEGORY_SET_URL, {
    token: t,
    email,
    category,
  });
  // Same reasoning as setDomain: the caller's own view of who they can
  // see may have just changed.
  invalidateDirectoryScope();
  return { ...data, users: data.users || [] };
}

export async function setDomain(
  domain: string,
  fields: { category?: DomainCategory | null; company?: string | null; visibleToExternal?: boolean }
): Promise<DomainsResponse> {
  const t = token();
  if (!t) throw new Error("Your session expired — sign out and back in, then try again");
  const raw = await postJson<DomainsResponse>(DOMAINS_SET_URL, { token: t, domain, ...fields });
  const data = { ...raw, users: raw.users || [] };
  // The caller's own view of who they can see may have just changed
  // (e.g. they classified their own domain) — drop the cached scope so
  // the next people-list reflects it instead of the pre-change answer.
  invalidateDirectoryScope();
  return data;
}

async function pollSince(sinceId: number): Promise<BroadcastNotification[]> {
  const t = token();
  if (!t) return [];
  const data = await postJson<{ notifications: BroadcastNotification[] }>(NOTIFY_POLL_URL, { token: t, sinceId });
  return data.notifications || [];
}

function loadHistory(): BroadcastNotification[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as BroadcastNotification[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(list: BroadcastNotification[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(-MAX_HISTORY)));
  } catch {
    /* ignore */
  }
}

function notExpired(n: BroadcastNotification): boolean {
  return Date.now() - new Date(n.created_at).getTime() < EXPIRY_MS;
}

/**
 * Polls for broadcast notifications, plays a sound + surfaces a toast for
 * anything genuinely new, and tracks an unread count for the bell badge.
 *
 * The very first poll a browser ever does is treated as a silent
 * baseline — it establishes "caught up as of here" without replaying
 * every historical announcement as a toast, so someone who logs in for
 * the first time after this feature ships doesn't get hit with a decade
 * of backlog all at once.
 */
export function useBroadcastNotifications() {
  const [history, setHistory] = useState<BroadcastNotification[]>(() => loadHistory());
  const [toast, setToast] = useState<BroadcastNotification | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const toastQueueRef = useRef<BroadcastNotification[]>([]);
  const toastTimerRef = useRef<number | null>(null);
  // Mirrors `toast` for the polling effect below, which intentionally has
  // an empty dependency array (so it subscribes exactly one interval for
  // the component's lifetime) — reading `toast` state directly there
  // would always see whatever it was at mount, never its later value.
  // Refs sidestep that; `.current` is always live regardless of when the
  // enclosing closure was created.
  const showingToastRef = useRef(false);
  // Forces a re-render periodically even when a poll finds nothing new, so
  // a notification that quietly crosses the 3-day mark while the app just
  // sits open disappears on its own rather than needing a reload to
  // re-evaluate notExpired.
  const [, setExpiryTick] = useState(0);

  const recomputeUnread = useCallback((list: BroadcastNotification[]) => {
    const lastRead = Number(localStorage.getItem(LAST_READ_KEY) || 0);
    setUnreadCount(list.filter((n) => n.id > lastRead && notExpired(n)).length);
  }, []);

  useEffect(() => {
    recomputeUnread(history);
    const interval = window.setInterval(() => setExpiryTick((t) => t + 1), 60_000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advanceToastQueue = useCallback(() => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    const next = toastQueueRef.current.shift();
    setToast(next || null);
    showingToastRef.current = !!next;
    if (next) {
      toastTimerRef.current = window.setTimeout(advanceToastQueue, 10_000);
    }
  }, []);

  const openToast = useCallback(
    (n: BroadcastNotification) => {
      localStorage.setItem(LAST_READ_KEY, String(n.id));
      recomputeUnread(history);
      advanceToastQueue();
    },
    [advanceToastQueue, history, recomputeUnread]
  );

  const markAllRead = useCallback(() => {
    const maxId = history.reduce((m, n) => Math.max(m, n.id), 0);
    localStorage.setItem(LAST_READ_KEY, String(maxId));
    setUnreadCount(0);
  }, [history]);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const storedLastPolled = localStorage.getItem(LAST_POLLED_KEY);
      const isFirstEver = storedLastPolled === null;
      const sinceId = Number(storedLastPolled || 0);
      let fresh: BroadcastNotification[];
      try {
        fresh = await pollSince(sinceId);
      } catch {
        return; // network hiccup — just try again next interval
      }
      if (cancelled || fresh.length === 0) return;

      const maxId = fresh.reduce((m, n) => Math.max(m, n.id), sinceId);
      localStorage.setItem(LAST_POLLED_KEY, String(maxId));

      // Computed from the updater's own `prev`, never the outer `history`
      // variable — this closure is created once (empty deps below) and
      // reused for every tick, so the outer `history` would otherwise be
      // stuck at whatever it was on the very first poll.
      setHistory((prev) => {
        const merged = [...prev, ...fresh];
        saveHistory(merged);

        if (isFirstEver) {
          // Silent baseline — caught up, but nothing to toast about.
          localStorage.setItem(LAST_READ_KEY, String(maxId));
          setUnreadCount(0);
        } else {
          const lastRead = Number(localStorage.getItem(LAST_READ_KEY) || 0);
          setUnreadCount(merged.filter((n) => n.id > lastRead && notExpired(n)).length);
        }
        return merged;
      });

      if (!isFirstEver) {
        toastQueueRef.current.push(...fresh);
        sounds.playOnce(sounds.SOUND.specialNotification);
        if (!showingToastRef.current) advanceToastQueue();
      }
    };

    void poll();
    const interval = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtered fresh on every render (not memoized on `history` alone) — the
  // periodic tick above is what makes something age out live, without
  // that this would only ever re-evaluate when new data actually arrives.
  const visibleHistory = history.filter(notExpired);

  return { history: visibleHistory, toast, unreadCount, openToast, markAllRead };
}
